<script setup lang="ts">
import { useIntervalFn } from '@vueuse/core'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import AiReportsList from '@/views/reporting/AiReportsList.vue'
import AiReportDialog from '@/views/reporting/AiReportDialog.vue'
import AiReportPreview from '@/views/reporting/AiReportPreview.vue'
import AiReportsDictionary from '@/views/reporting/AiReportsDictionary.vue'
import { useAiReportsStore } from '@/views/reporting/useAiReportsStore'
import type { ReportSummary } from '@/views/reporting/types'

definePage({
  meta: {
    action: 'read',
    subject: 'games',
  },
})

/** Роли, которым фича открыта. Тот же набор стоит на роутах модуля и в снапшоте фич. */
const ALLOWED_ROLES = ['admin', 'scoped_admin', 'manager']

/**
 * Опрос списка, пока собирается файл: событие `ai-report.status_changed` может не доехать
 * (Reverb лёг) — это страховка, а не основной путь.
 *
 * **Не 7 секунд, как было.** Норма `ai-reports-read` — 60 запросов в минуту **на всё
 * чтение раздела**, и с переездом хода диалога в очередь этот бюджет делится: там теперь
 * свой опрос-страховка состояния хода. Семь секунд — это 8.6 запросов в минуту на список,
 * который меняется раз в минуту (столько идёт сборка файла), то есть почти вся частота
 * тратилась на подтверждение, что ничего не изменилось. Пятнадцать секунд стоят вчетверо
 * меньше и задерживают появление готового файла на те же секунды — а обычно не задерживают
 * вовсе, потому что первым приходит событие.
 */
const PENDING_POLL_MS = 15000

const { t } = useI18n()
const router = useRouter()
const store = useAiReportsStore()

const userData = useCookie<any>('userData')
const orgSettings = useCookie<any>('organizationSettings')

/*
 * Тот же снапшот фичи, что гейтит пункт меню (`DefaultLayoutWithVerticalNav`), плюс явная
 * проверка роли. Это не защита — бэкенд на выключенной фиче отвечает 403, а роль `head`
 * не пускает `role:`-middleware, — а чтобы прямой заход по адресу не показывал раздел,
 * все ручки которого откажут. Роль проверяется отдельно от снапшота намеренно: снапшот
 * ставится при логине и на роль `head` пустой, но полагаться на один источник там, где
 * ошибка означает показанный не тому разделу экран, не стоит.
 */
const roles: string[] = userData.value?.roles ?? []
const featureEnabled = (orgSettings.value?.enabledFeatures ?? []).includes('ai-reports')

if (!featureEnabled || !roles.some(role => ALLOWED_ROLES.includes(role)))
  await router.replace('/games/list')

const isSnackbarVisible = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const isDictionaryVisible = ref(false)
const isConfirmDeleteVisible = ref(false)
const reportToDelete = ref<ReportSummary | null>(null)

const notify = (text: string, color = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  isSnackbarVisible.value = true
}

/*
 * Продуктовые отказы модуля несут готовый текст в `message` и машинный `code` в теле.
 * Показываем текст только у них: 403 выключенной фичи и 5xx отдают служебные строки
 * Laravel, читать которые человеку нечего. 429 — отдельный текст: это не ошибка запроса,
 * а просьба подождать, и общий «что-то пошло не так» уводил бы в неверную сторону.
 */
const refusalText = (error: any, fallback: string = t('ai-reports-page.errors.generic')): string => {
  const status = error?.statusCode ?? error?.response?.status

  if (status === 429)
    return t('ai-reports-page.errors.rate_limit')

  if (status !== 422 && status !== 404 && status !== 409)
    return fallback

  return error?.data?.message ?? error?.response?._data?.message ?? fallback
}

const onCreate = async () => {
  // Заявка заводится первой фразой человека (её текст становится названием карточки),
  // поэтому «новый отчёт» — это просто чистый диалог, а не запрос к серверу.
  await store.selectReport(null)
}

const onSelect = async (id: string) => {
  await store.selectReport(id)
}

const onRemove = (report: ReportSummary) => {
  reportToDelete.value = report
  isConfirmDeleteVisible.value = true
}

const confirmDelete = async () => {
  const report = reportToDelete.value

  if (!report)
    return

  try {
    await store.deleteReport(report.id)
    notify(t('ai-reports-page.list.deleted'))
  }
  catch (error: any) {
    notify(refusalText(error), 'error')
  }
  finally {
    reportToDelete.value = null
  }
}

const onSend = async (text: string) => {
  try {
    await store.sendText(text)
  }
  catch (error: any) {
    notify(refusalText(error), 'error')
  }
}

const onResolve = async (optionId: string, optionTitle: string) => {
  await store.sendResolution(optionId, optionTitle)
}

const onGenerate = async () => {
  const report = store.activeReport

  if (!report)
    return

  try {
    await store.generateReport(report.id)
    notify(t('ai-reports-page.preview.queued'))
  }
  catch (error: any) {
    notify(refusalText(error), 'error')
  }
}

const onDownload = async () => {
  const report = store.activeReport

  if (!report)
    return

  try {
    await store.downloadReport(report.id, 'ai-report.xlsx')

    // Срок жизни файла приезжает с сервера вместе с заявкой. Скачивать можно только
    // открытую заявку, поэтому к этому моменту он известен; напоминание без числа было бы
    // хуже его отсутствия, поэтому при неизвестном сроке снекбар не показывается.
    if (store.limits)
      notify(t('ai-reports-page.preview.ttl_warning', { days: store.limits.fileTtlDays }), 'info')
  }
  catch (error: any) {
    notify(refusalText(error, t('ai-reports-page.errors.download')), 'error')

    // 404 здесь означает «файла больше нет»: срок вышел либо объект подмели. Список
    // перечитывается, чтобы карточка перестала предлагать скачивание.
    await store.fetchReports()
  }
}

const { pause, resume } = useIntervalFn(async () => {
  await store.fetchReports()
}, PENDING_POLL_MS, { immediate: false })

watch(() => store.hasPendingGeneration, isPending => {
  if (isPending)
    resume()
  else
    pause()
})

onMounted(async () => {
  await Promise.all([store.fetchReports(), store.fetchDictionary()])

  /*
   * После перезагрузки страницы экран возвращается туда, где человек был: список
   * отсортирован по времени изменения, поэтому первая карточка — та, с которой он только
   * что говорил. Раньше открывать было нечего (переписка жила в памяти вкладки), и заход
   * всегда начинался с чистого диалога; теперь чистый диалог — это осознанное «Новый отчёт»,
   * а не единственное, что умеет показать страница.
   */
  if (!store.activeId && store.reports.length)
    await store.selectReport(store.reports[0].id)

  if (userData.value?.id)
    store.subscribe(userData.value.id)

  /*
   * Стор переживает уход со страницы, а таймеры ожидания хода с неё снимаются, — значит
   * возврат обязан их завести заново. Иначе человек, ушедший в другой раздел посреди хода
   * и вернувшийся, видел бы заблокированную отправку и ждал бы вечно: событие он мог
   * пропустить, а опрашивать было бы уже некому.
   */
  store.resumeTurnWaits()

  if (store.hasPendingGeneration)
    resume()
})

onUnmounted(() => {
  pause()

  // Оба фоновых опроса раздела снимаются здесь, и по одной причине: оба ходят по норме
  // `ai-reports-read`, а страницы, ради которой они ходят, больше нет.
  store.pauseTurnWaits()

  if (userData.value?.id)
    store.unsubscribe(userData.value.id)
})
</script>

<template>
  <div>
    <div class="mb-4">
      <h4 class="text-h4">
        {{ t('ai-reports-page.title') }}
      </h4>
      <p class="text-body-2 text-medium-emphasis mb-0">
        {{ t('ai-reports-page.subtitle') }}
      </p>
    </div>

    <VRow>
      <VCol
        cols="12"
        md="4"
      >
        <AiReportsList
          @create="onCreate"
          @select="onSelect"
          @remove="onRemove"
        />
      </VCol>

      <VCol
        cols="12"
        md="8"
      >
        <AiReportDialog
          @send="onSend"
          @resolve="onResolve"
          @retry="store.retryTurn"
          @dismiss="store.dismissTurn"
          @open-dictionary="isDictionaryVisible = true"
        />
      </VCol>

      <VCol cols="12">
        <AiReportPreview
          @generate="onGenerate"
          @download="onDownload"
        />
      </VCol>
    </VRow>

    <AiReportsDictionary v-model:is-dialog-visible="isDictionaryVisible" />

    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDeleteVisible"
      :confirmation-question="t('ai-reports-page.list.delete_confirm')"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="confirmDelete"
    />

    <VSnackbar
      v-model="isSnackbarVisible"
      location="top end"
      :color="snackbarColor"
    >
      {{ snackbarText }}
    </VSnackbar>
  </div>
</template>
